import { buildAuthenticatedHeaders } from './authStorage.ts';
import type {
  AppClient,
  AppSite,
  AppTechnician,
  AppWorkOrderDetail,
  AppWorkOrderStatus,
  CreateAppWorkOrderInput,
  UpdateAppWorkOrderInput,
} from './apiContract.ts';
export type {
  AppClient,
  AppSite,
  AppTechnician,
  AppWorkOrderDetail,
  AppWorkOrderStatus,
  CreateAppWorkOrderInput,
  UpdateAppWorkOrderInput,
} from './apiContract.ts';

export type AppWorkOrderFilters = Partial<{
  status: AppWorkOrderStatus;
  technicianId: string;
  clientId: string;
  query: string;
}>;

function getAppApiBaseUrl(): string {
  const raw = String(import.meta.env.VITE_DEMO_API_BASE_URL || '/api').trim();
  if (!raw) return '/api';
  return raw.endsWith('/') ? raw.slice(0, -1) : raw;
}

async function requestAppJson<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${getAppApiBaseUrl()}${path}`, {
    ...init,
    credentials: 'include',
    headers: buildAuthenticatedHeaders({
      Accept: 'application/json',
      ...(init?.headers || {}),
    }),
  });

  const contentType = response.headers.get('content-type') || '';
  if (!response.ok) {
    let detail = response.statusText;
    if (contentType.includes('application/json')) {
      const payload = (await response.json()) as { detail?: string; error?: string };
      detail = payload.detail || payload.error || detail;
    } else {
      detail = (await response.text()) || detail;
    }
    throw new Error(`HTTP ${response.status}: ${detail}`);
  }
  if (!contentType.includes('application/json')) {
    throw new Error(`Type de contenu inattendu renvoyé par l’API applicative : ${contentType || 'inconnu'}`);
  }

  return (await response.json()) as T;
}

function buildSearchParams(filters?: AppWorkOrderFilters): string {
  const params = new URLSearchParams();
  if (filters?.status) params.set('status', filters.status);
  if (filters?.technicianId) params.set('technicianId', filters.technicianId);
  if (filters?.clientId) params.set('clientId', filters.clientId);
  if (filters?.query) params.set('q', filters.query);

  const encoded = params.toString();
  return encoded ? `?${encoded}` : '';
}

export async function fetchAppClients(signal?: AbortSignal): Promise<AppClient[]> {
  return requestAppJson<AppClient[]>('/app/clients', { signal });
}

export async function fetchAppSites(clientId?: string, signal?: AbortSignal): Promise<AppSite[]> {
  const query = clientId ? `?clientId=${encodeURIComponent(clientId)}` : '';
  return requestAppJson<AppSite[]>(`/app/sites${query}`, { signal });
}

export async function fetchAppTechnicians(signal?: AbortSignal): Promise<AppTechnician[]> {
  return requestAppJson<AppTechnician[]>('/app/technicians', { signal });
}

export async function fetchAppWorkOrders(filters?: AppWorkOrderFilters, signal?: AbortSignal): Promise<AppWorkOrderDetail[]> {
  return requestAppJson<AppWorkOrderDetail[]>(`/app/work-orders${buildSearchParams(filters)}`, { signal });
}

export async function createAppWorkOrder(payload: CreateAppWorkOrderInput): Promise<AppWorkOrderDetail> {
  return requestAppJson<AppWorkOrderDetail>('/app/work-orders', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
}

export async function updateAppWorkOrder(
  workOrderId: string,
  payload: UpdateAppWorkOrderInput,
): Promise<AppWorkOrderDetail> {
  return requestAppJson<AppWorkOrderDetail>(`/app/work-orders/${encodeURIComponent(workOrderId)}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
}

export async function duplicateAppWorkOrder(workOrderId: string): Promise<AppWorkOrderDetail> {
  return requestAppJson<AppWorkOrderDetail>(`/app/work-orders/${encodeURIComponent(workOrderId)}/duplicate`, {
    method: 'POST',
  });
}
